import { GamesRequestParams } from '../types/GamesRequestParams';

// The player's last filters, restored when the app is reopened. Excludes `page` and the one-off
// deep-link params `pattern`/`repair`/`pgn`.
const FILTERS_KEY = 'syc.lastFilters';
const PERSISTED_KEYS: (keyof GamesRequestParams)[] = [
  'username',
  'color',
  'result',
  'platform',
  'opening',
  'timeControl',
  'from',
  'to',
  'ratingFrom',
  'ratingTo',
  'speed',
  'onlyStarred',
  'favoriteMoves',
  'hasComments',
  'commentedByMe',
  'group',
];

// Every access is wrapped: localStorage throws in Safari private mode and when storage is blocked.
export function readSavedFilters(): GamesRequestParams {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (raw == null) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed == null || typeof parsed !== 'object') {
      return {};
    }
    const out: Record<string, unknown> = {};
    for (const key of PERSISTED_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (value != null) {
        out[key] = value;
      }
    }
    return out as GamesRequestParams;
  } catch {
    return {};
  }
}

export function writeSavedFilters(params: GamesRequestParams): void {
  try {
    const out: Record<string, unknown> = {};
    for (const key of PERSISTED_KEYS) {
      const value = params[key];
      if (value != null) {
        out[key] = value;
      }
    }
    localStorage.setItem(FILTERS_KEY, JSON.stringify(out));
  } catch {
    // Storage unavailable — filters just won't survive the session.
  }
}

// Only the first load in a tab restores. The logo is a plain `<a href="/">` reload, so clicking it
// mid-session still gives a clean view; sessionStorage survives the reload but not a closed tab.
const RESTORED_FLAG_KEY = 'syc.filtersRestoredThisTab';

export function readFiltersToRestore(): GamesRequestParams {
  try {
    if (sessionStorage.getItem(RESTORED_FLAG_KEY) != null) {
      return {};
    }
    sessionStorage.setItem(RESTORED_FLAG_KEY, '1');
  } catch {
    // Fall through and restore anyway — losing the "only once per tab" guard is harmless.
  }
  return readSavedFilters();
}
